/**
 * Album Model - Gallery v2
 * 
 * Represents an album in the unified gallery system.
 * Supports hierarchy, permissions, and various album types.
 */

import { Timestamp } from 'firebase/firestore';

/**
 * Album type enumeration
 */
export enum AlbumType {
  ROOT = 'root',                     // Top-level container
  SYSTEM = 'system',                 // "Playlists", "Field Flights", "Members"
  FIELD_FLIGHTS = 'field-flights',   // Date-based flight photos
  MEMBER_PERSONAL = 'member-personal', // Individual member albums
  CUSTOM = 'custom',                 // User-created albums
  PHOTONLINK = 'photonlink'          // Synced from PhotonLink
}

/**
 * Album visibility options
 */
export type AlbumVisibility = 'public' | 'members' | 'officers' | 'private';

/**
 * Child reference in album's children array
 */
export interface AlbumChild {
  type: 'album' | 'media';
  id: string;
}

/**
 * Album interface
 */
export interface Album {
  id: string;                      // Firestore document ID

  // Basic Information
  name: string;                    // "Field Flights", "Members", "Playlists"
  description?: string | null;     // Album description
  slug: string;                    // URL-friendly name: "field-flights"

  // Hierarchy
  parentId?: string | null;        // Parent album reference (legacy field, kept for compatibility)
  parentAlbumId?: string | null;   // Parent album reference (null = root)
  albumPath: string;               // Full path: "field-flights/2024-12-25" (legacy, kept for compatibility)
  children?: AlbumChild[];         // Direct children (albums and media)
  depth: number;                   // Nesting level (0 = root)
  sortOrder: number;               // Display order within parent
  sortName?: string | null;        // Optional sort name for custom ordering (e.g., "20251223" for Field Flights dates)

  // Metadata
  coverMediaId?: string | null;           // Featured media item for cover
  coverUrl?: string | null;               // Fallback cover image URL
  mediaCount: number;              // Cached count of media items
  viewCount: number;               // Number of views

  // Permissions & Ownership
  albumType: AlbumType;            // See AlbumType enum
  ownerId?: string | null;                // User ID for personal albums
  ownerName?: string | null;              // Denormalized for display
  createdBy: string;               // User ID who created

  // Album-specific features
  autoDateDetection?: boolean;     // For Field Flights sub-albums
  dateFormat?: string | null;             // "YYYY-MM-DD" for date-based albums
  albumDate?: string | null;              // ISO date for date-based albums

  // Source tracking
  source: 'firebase' | 'photonlink'; // Where the album originates
  photonlinkAlbumId?: string | null;      // If synced from PhotonLink

  // Visibility
  visibility: AlbumVisibility;
  isArchived: boolean;             // Hidden from main view

  // Timestamps
  createdAt: Timestamp;
  updatedAt: Timestamp;
  lastMediaAddedAt?: Timestamp | null;
}

/**
 * Create a new Album with default values
 */
export function createAlbum(data: Partial<Album> & Pick<Album, 'name' | 'albumType' | 'createdBy'>): Omit<Album, 'id'> {
  const slug = data.slug || slugify(data.name);
  const now = Timestamp.now();

  return {
    name: data.name,
    description: data.description || '',
    slug,
    parentId: data.parentAlbumId || null,
    parentAlbumId: data.parentAlbumId || null,
    albumPath: data.albumPath || slug,
    depth: data.depth || 0,
    sortOrder: data.sortOrder || 0,
    sortName: data.sortName || null,
    coverMediaId: data.coverMediaId || null,
    coverUrl: data.coverUrl || null,
    mediaCount: data.mediaCount || 0,
    viewCount: data.viewCount || 0,
    albumType: data.albumType,
    ownerId: data.ownerId || null,
    ownerName: data.ownerName || null,
    createdBy: data.createdBy,
    autoDateDetection: data.autoDateDetection || false,
    dateFormat: data.dateFormat || null,
    albumDate: data.albumDate || null,
    source: data.source || 'firebase',
    photonlinkAlbumId: data.photonlinkAlbumId || null,
    visibility: data.visibility || 'members',
    isArchived: data.isArchived || false,
    createdAt: data.createdAt || now,
    updatedAt: data.updatedAt || now,
    lastMediaAddedAt: data.lastMediaAddedAt || null,
  };
}

/**
 * Generate URL-friendly slug from album name
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Normalize album path by removing leading slash
 * Handles legacy paths that may have been created with leading slashes
 */
export function normalizeAlbumPath(path: string): string {
  return path.startsWith('/') ? path.substring(1) : path;
}

/**
 * Calculate album path from parent path and slug
 */
export function buildAlbumPath(parentPath: string | null, slug: string): string {
  if (!parentPath) {
    return slug;
  }
  // Ensure parent path has no leading slash
  const normalizedParent = normalizeAlbumPath(parentPath);
  return `${normalizedParent}/${slug}`;
}

/**
 * Check if user can edit album
 */
export function canEditAlbum(
  album: Album,
  userId: string,
  isOfficer: boolean,
  isAdmin: boolean
): boolean {
  // Admins and officers can edit any album
  if (isAdmin || isOfficer) {
    return true;
  }

  // Users can edit their personal albums
  if (album.albumType === AlbumType.MEMBER_PERSONAL && album.ownerId === userId) {
    return true;
  }

  return false;
}

/**
 * Check if user can upload to album
 */
export function canUploadToAlbum(
  album: Album,
  userId: string,
  isMember: boolean,
  isOfficer: boolean,
  isAdmin: boolean
): boolean {
  // Admins and officers can upload to any album
  if (isAdmin || isOfficer) {
    return true;
  }

  // Members can upload to their personal albums
  if (album.albumType === AlbumType.MEMBER_PERSONAL && album.ownerId === userId) {
    return true;
  }

  // Members can upload to public Field Flights albums
  if (isMember && album.albumType === AlbumType.FIELD_FLIGHTS) {
    return true;
  }

  // Members can upload to custom public albums
  if (isMember && album.albumType === AlbumType.CUSTOM && album.visibility === 'public') {
    return true;
  }

  return false;
}

/**
 * Check if user can view album
 */
export function canViewAlbum(
  album: Album,
  userId: string | null,
  isMember: boolean,
  isOfficer: boolean,
  isAdmin: boolean
): boolean {
  // Public albums are visible to everyone
  if (album.visibility === 'public') {
    return true;
  }

  // Admins and officers can view all albums
  if (isAdmin || isOfficer) {
    return true;
  }

  // Members can view member-only albums
  if (album.visibility === 'members' && isMember) {
    return true;
  }

  // Officers can view officer-only albums (already handled above)
  if (album.visibility === 'officers' && isOfficer) {
    return true;
  }

  // Users can view their own private albums
  if (album.visibility === 'private' && album.ownerId === userId) {
    return true;
  }

  return false;
}
